import { N_UrlResource } from "@specsheet-common/shared-types";
import { formatUrl } from "../url";

const IMPORTANT_INFO_ATTR_NAME = "Important info";

/** Bare www./http(s) URLs in attribute text (same idea as GFM autolinks). */
const URL_IN_TEXT_REGEX = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;

type PlacementAttributeSource = {
  name: string;
  value: string;
};

export type PlacementDocumentsSource = {
  documents?: N_UrlResource[] | null;
  originalSpecs?: N_UrlResource[] | null;
  attributes?: PlacementAttributeSource[] | null;
};

export const documentsColumnSource = {
  documents: "documents",
  originalSpecs: "originalSpecs",
  additionalInfo: "additionalInfo",
} as const;

export type DocumentsColumnSource =
  (typeof documentsColumnSource)[keyof typeof documentsColumnSource];

export const documentsColumnSourceLabels: Record<
  DocumentsColumnSource,
  string
> = {
  documents: "Documents",
  originalSpecs: "Original Specs",
  additionalInfo: "Additional Info",
};

/** Bit flags for fast filter membership (avoid array/Set alloc on hot paths). */
export const documentsColumnSourceMask = {
  documents: 1 << 0,
  originalSpecs: 1 << 1,
  additionalInfo: 1 << 2,
} as const;

const ALL_SOURCES_MASK =
  documentsColumnSourceMask.documents |
  documentsColumnSourceMask.originalSpecs |
  documentsColumnSourceMask.additionalInfo;

const SOURCE_FROM_MASK: Array<[number, DocumentsColumnSource]> = [
  [documentsColumnSourceMask.documents, documentsColumnSource.documents],
  [
    documentsColumnSourceMask.originalSpecs,
    documentsColumnSource.originalSpecs,
  ],
  [
    documentsColumnSourceMask.additionalInfo,
    documentsColumnSource.additionalInfo,
  ],
];

const EMPTY_URL_RESOURCES: N_UrlResource[] = [];

const stripTrailingUrlPunctuation = (url: string): string =>
  url.replace(/[.,;:!?)\]}>]+$/g, "");

const hasUrlResources = (resources?: N_UrlResource[] | null): boolean => {
  if (!resources?.length) {
    return false;
  }
  for (let i = 0; i < resources.length; i++) {
    if (resources[i].data?.value) {
      return true;
    }
  }
  return false;
};

const compareResourceName = (a: N_UrlResource, b: N_UrlResource): number =>
  (a.name ?? "").localeCompare(b.name ?? "", undefined, {
    sensitivity: "base",
    numeric: true,
  });

export const createUrlResource = (
  name: string | null,
  href: string
): N_UrlResource => ({
  name,
  data: { kind: "url", value: href },
  notes: null,
  fileSize: null,
});

const formatDocumentNameFromNormalizedHref = (href: string): string => {
  try {
    const parsed = new URL(href);
    const pathname = parsed.pathname;
    let lastSlash = pathname.lastIndexOf("/");
    let rawSegment =
      lastSlash >= 0 && lastSlash < pathname.length - 1
        ? pathname.slice(lastSlash + 1)
        : pathname.replace(/\//g, "") || parsed.hostname;
    // Drop trailing slash segment
    if (!rawSegment && lastSlash > 0) {
      const prev = pathname.lastIndexOf("/", lastSlash - 1);
      rawSegment = pathname.slice(prev + 1, lastSlash) || parsed.hostname;
    }
    const withSpaces = decodeURIComponent(rawSegment).replace(/-/g, " ").trim();
    if (!withSpaces) {
      return href;
    }
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  } catch {
    return href;
  }
};

export const formatDocumentNameFromUrlPath = (url: string): string => {
  try {
    const href = formatUrl(url);
    return formatDocumentNameFromNormalizedHref(href);
  } catch {
    return url;
  }
};

/** First-wins dedupe by href. Returns the same array reference when length ≤ 1. */
export const dedupeUrlResourcesByHref = (
  resources: N_UrlResource[]
): N_UrlResource[] => {
  if (resources.length <= 1) {
    return resources;
  }

  const byUrl = new Map<string, N_UrlResource>();
  for (let i = 0; i < resources.length; i++) {
    const resource = resources[i];
    const url = resource.data?.value;
    if (!url || byUrl.has(url)) {
      continue;
    }
    byUrl.set(url, resource);
  }

  return byUrl.size === resources.length ? resources : [...byUrl.values()];
};

/** Alphabetical by name within a source group (stable identifyability). */
export const sortUrlResourcesByName = (
  resources: N_UrlResource[]
): N_UrlResource[] => {
  if (resources.length <= 1) {
    return resources;
  }
  return [...resources].sort(compareResourceName);
};

/**
 * Merge source groups in display order, sorting alphabetically within each group.
 * Cross-source URL dedupe is first-wins (documents beat originalSpecs beat additional info).
 */
const mergeDocumentSourceGroups = (
  documents: N_UrlResource[],
  originalSpecs: N_UrlResource[],
  additionalInfo: N_UrlResource[]
): N_UrlResource[] => {
  const sortedDocuments = sortUrlResourcesByName(documents);
  const sortedOriginalSpecs = sortUrlResourcesByName(originalSpecs);
  const sortedAdditionalInfo = sortUrlResourcesByName(additionalInfo);

  if (sortedOriginalSpecs.length === 0 && sortedAdditionalInfo.length === 0) {
    return sortedDocuments;
  }
  if (sortedDocuments.length === 0 && sortedAdditionalInfo.length === 0) {
    return sortedOriginalSpecs;
  }
  if (sortedDocuments.length === 0 && sortedOriginalSpecs.length === 0) {
    return sortedAdditionalInfo;
  }

  // Single concat alloc instead of nested spreads in callers
  const merged = new Array<N_UrlResource>(
    sortedDocuments.length +
      sortedOriginalSpecs.length +
      sortedAdditionalInfo.length
  );
  let offset = 0;
  for (let i = 0; i < sortedDocuments.length; i++) {
    merged[offset++] = sortedDocuments[i];
  }
  for (let i = 0; i < sortedOriginalSpecs.length; i++) {
    merged[offset++] = sortedOriginalSpecs[i];
  }
  for (let i = 0; i < sortedAdditionalInfo.length; i++) {
    merged[offset++] = sortedAdditionalInfo[i];
  }

  return dedupeUrlResourcesByHref(merged);
};

const getImportantInfoAttributeValue = (
  attributes?: PlacementAttributeSource[] | null
): string | undefined => {
  if (!attributes?.length) {
    return undefined;
  }
  for (let i = 0; i < attributes.length; i++) {
    if (attributes[i].name === IMPORTANT_INFO_ATTR_NAME) {
      return attributes[i].value;
    }
  }
  return undefined;
};

export const extractImportantInfoUrlResources = (
  attributes?: PlacementAttributeSource[] | null
): N_UrlResource[] => {
  const importantInfo = getImportantInfoAttributeValue(attributes);
  if (!importantInfo) {
    return EMPTY_URL_RESOURCES;
  }

  URL_IN_TEXT_REGEX.lastIndex = 0;
  const matches = importantInfo.match(URL_IN_TEXT_REGEX);
  if (!matches?.length) {
    return EMPTY_URL_RESOURCES;
  }

  const resources: N_UrlResource[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < matches.length; i++) {
    const href = formatUrl(stripTrailingUrlPunctuation(matches[i]));
    if (!href || seen.has(href)) {
      continue;
    }
    seen.add(href);
    resources.push(
      createUrlResource(formatDocumentNameFromNormalizedHref(href), href)
    );
  }

  return resources.length === 0 ? EMPTY_URL_RESOURCES : resources;
};

/**
 * Fast presence check for Important info URLs (no URL normalize / resource alloc).
 * Used by filter source detection where rowsCount runs this over every booking.
 */
export const placementHasImportantInfoUrls = (
  attributes?: PlacementAttributeSource[] | null
): boolean => {
  const importantInfo = getImportantInfoAttributeValue(attributes);
  if (!importantInfo) {
    return false;
  }

  URL_IN_TEXT_REGEX.lastIndex = 0;
  return URL_IN_TEXT_REGEX.test(importantInfo);
};

/** Bitmask of document sources present on a placement (0 = none). */
export const getDocumentsColumnSourceMaskForPlacement = (
  placement: PlacementDocumentsSource
): number => {
  let mask = 0;
  if (hasUrlResources(placement.documents)) {
    mask |= documentsColumnSourceMask.documents;
  }
  if (hasUrlResources(placement.originalSpecs)) {
    mask |= documentsColumnSourceMask.originalSpecs;
  }
  if (placementHasImportantInfoUrls(placement.attributes)) {
    mask |= documentsColumnSourceMask.additionalInfo;
  }
  return mask;
};

/**
 * Bitmask of sources across placements. Early-exits once all three sources are found.
 */
export const getDocumentsColumnSourceMaskForPlacements = (
  placements: PlacementDocumentsSource[] | null | undefined
): number => {
  if (!placements?.length) {
    return 0;
  }
  let mask = 0;
  for (let i = 0; i < placements.length; i++) {
    mask |= getDocumentsColumnSourceMaskForPlacement(placements[i]);
    if (mask === ALL_SOURCES_MASK) {
      break;
    }
  }
  return mask;
};

export const documentsColumnSourceIdToMask = (
  sourceId: string
): number | undefined => {
  switch (sourceId) {
    case documentsColumnSource.documents:
      return documentsColumnSourceMask.documents;
    case documentsColumnSource.originalSpecs:
      return documentsColumnSourceMask.originalSpecs;
    case documentsColumnSource.additionalInfo:
      return documentsColumnSourceMask.additionalInfo;
    default:
      return undefined;
  }
};

/**
 * Which Documents-column sources have data on this placement (field presence,
 * independent of merge/dedupe order). Used for filtering.
 */
export const getDocumentsColumnSourcesForPlacement = (
  placement: PlacementDocumentsSource
): DocumentsColumnSource[] => {
  const mask = getDocumentsColumnSourceMaskForPlacement(placement);
  if (mask === 0) {
    return [];
  }
  const sources: DocumentsColumnSource[] = [];
  for (let i = 0; i < SOURCE_FROM_MASK.length; i++) {
    const [bit, source] = SOURCE_FROM_MASK[i];
    if (mask & bit) {
      sources.push(source);
    }
  }
  return sources;
};

/** Sources present across any placement in a booking row. */
export const getDocumentsColumnSourcesForPlacements = (
  placements: PlacementDocumentsSource[]
): DocumentsColumnSource[] => {
  const mask = getDocumentsColumnSourceMaskForPlacements(placements);
  if (mask === 0) {
    return [];
  }
  const sources: DocumentsColumnSource[] = [];
  for (let i = 0; i < SOURCE_FROM_MASK.length; i++) {
    const [bit, source] = SOURCE_FROM_MASK[i];
    if (mask & bit) {
      sources.push(source);
    }
  }
  return sources;
};

/**
 * Display-only Documents column resources:
 * documents → originalSpecs → Important info URLs.
 * Alphabetically sorted within each source group. Dedupes by URL (first wins).
 */
export const getDocumentsColumnResourcesForPlacement = (
  placement: PlacementDocumentsSource
): N_UrlResource[] => {
  const documents = placement.documents ?? EMPTY_URL_RESOURCES;
  const originalSpecs = placement.originalSpecs ?? EMPTY_URL_RESOURCES;

  // Skip Important-info parsing when there is clearly no URL text
  const importantInfoUrls = placementHasImportantInfoUrls(placement.attributes)
    ? extractImportantInfoUrlResources(placement.attributes)
    : EMPTY_URL_RESOURCES;

  return mergeDocumentSourceGroups(documents, originalSpecs, importantInfoUrls);
};

/**
 * Merge Documents column resources across placements in a booking row.
 * Keeps global source grouping: all documents, then all originalSpecs, then additional info.
 */
export const getDocumentsColumnResourcesForPlacements = (
  placements: PlacementDocumentsSource[]
): N_UrlResource[] => {
  if (placements.length === 0) {
    return EMPTY_URL_RESOURCES;
  }
  if (placements.length === 1) {
    return getDocumentsColumnResourcesForPlacement(placements[0]);
  }

  const documents: N_UrlResource[] = [];
  const originalSpecs: N_UrlResource[] = [];
  const additionalInfo: N_UrlResource[] = [];

  for (let i = 0; i < placements.length; i++) {
    const placement = placements[i];
    if (placement.documents?.length) {
      for (let j = 0; j < placement.documents.length; j++) {
        documents.push(placement.documents[j]);
      }
    }
    if (placement.originalSpecs?.length) {
      for (let j = 0; j < placement.originalSpecs.length; j++) {
        originalSpecs.push(placement.originalSpecs[j]);
      }
    }
    if (placementHasImportantInfoUrls(placement.attributes)) {
      const extracted = extractImportantInfoUrlResources(placement.attributes);
      for (let j = 0; j < extracted.length; j++) {
        additionalInfo.push(extracted[j]);
      }
    }
  }

  return mergeDocumentSourceGroups(
    dedupeUrlResourcesByHref(documents),
    dedupeUrlResourcesByHref(originalSpecs),
    dedupeUrlResourcesByHref(additionalInfo)
  );
};
